import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CreditCard, FileText, FileCheck, Receipt, Download, Plus, Trash2, Eye, Loader2 } from 'lucide-react';
import { format, parseISO, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { getAuthToken } from '../../lib/authToken';
import { FactureEditDialog } from '../../components/facturation/FactureEditDialog';
import { FactureVerrouActions } from '../../components/facturation/FactureVerrouActions';
import { NumeroDocumentField } from '../../components/facturation/NumeroDocumentField';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITES_JOUR = ['journee', 'heure_formation', 'heure_stagiaire'];

const fmtFr = (d) => format(parseISO(d), 'dd/MM/yyyy');

// Regroupe des dates (yyyy-MM-dd) en périodes hebdomadaires
export const buildWeeklyPeriods = (dates) => {
  const byWeek = {};
  [...dates].sort().forEach(d => {
    const wk = format(startOfWeek(parseISO(d), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    (byWeek[wk] = byWeek[wk] || []).push(d);
  });
  return Object.keys(byWeek).sort().map(wk => {
    const days = byWeek[wk];
    return `Semaine du ${fmtFr(days[0])} au ${fmtFr(days[days.length - 1])} — ${days.length} jour(s)`;
  });
};

// ============== FACTURATION TAB (Phase 4) ==============
const FacturationTab = ({ dossierId, formData, financeurs, refreshKey, mode = 'af', autoFacture = null, onChanged }) => {
  const prestation = mode === 'prestation';
  const [documents, setDocuments] = useState([]);
  const [dossiersFinanceurs, setDossiersFinanceurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [factureDialog, setFactureDialog] = useState(null); // df concerné
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [manualDialog, setManualDialog] = useState(null); // df concerné
  const [manualLignes, setManualLignes] = useState([]);
  const [manualMeta, setManualMeta] = useState({ periode: '', bdc_idx: [], numero: '', numeroValide: true }); // période facturée + BDC rattachés + numéro (facture libre)
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);
  const [tauxTva, setTauxTva] = useState(20); // taux en vigueur (fiche organisme), applique par le backend
  useEffect(() => { axios.get(`${API_URL}/admin/taux-tva`, { headers }).then(r => setTauxTva(Number(r.data.taux_tva) || 0)).catch(() => {}); }, [headers]);

  // Mois couverts par le planning: { '2026-03': [dates...] }
  const planningMonths = useMemo(() => {
    const months = {};
    (formData.planning || []).forEach(p => {
      if (p.date) (months[p.date.slice(0, 7)] = months[p.date.slice(0, 7)] || []).push(p.date);
    });
    return months;
  }, [formData.planning]);

  const invoicedPeriods = useCallback((dfId) => new Set(
    documents.filter(d => d.dossier_financeur_id === dfId && d.type_document === 'facture' && d.statut !== 'annule' && d.periode).map(d => d.periode)
  ), [documents]);

  const [preview, setPreview] = useState(null); // { url, numero, nom } : apercu PDF avant edition
  const [editingFacture, setEditingFacture] = useState(null);
  const [revue, setRevue] = useState(null); // { df, typeDoc, items[] } : revue des prix / quantites avant generation
  const setRevueItem = (i, patch) => setRevue(r => ({ ...r, items: r.items.map((x, j) => j === i ? { ...x, ...patch } : x) }));
  const revueTotal = (revue?.items || []).reduce((t, it) => t + (Number(it.quantite) || 0) * (Number(it.prix_unitaire_ht) || 0), 0);
  const openPreview = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/documents/${doc.id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreview({ url, numero: doc.numero, nom: doc.destinataire_nom, doc });
    } catch (e) { toast.error('Impossible de charger l\'aperçu'); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };
  const nomFichier = (doc) => ['facture', 'avoir'].includes(doc.type_document) && doc.destinataire_nom ? `${doc.numero} - ${doc.destinataire_nom.replace(/[\\/:*?"<>|]/g, '')}.pdf` : `${doc.numero || 'document'}.pdf`;
  const downloadPdf = async (docId, numero, filenameOverride) => {
    const filename = filenameOverride || `${numero || 'document'}.pdf`;
    toast.info(`Téléchargement de ${filename}...`);
    try {
      const response = await axios.get(`${API_URL}/documents/${docId}/pdf`, {
        headers,
        responseType: 'blob',
      });
      saveAs(new Blob([response.data], { type: 'application/pdf' }), filename);
    } catch (err) {
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [docsRes, dfsRes] = await Promise.all([
        axios.get(`${API_URL}/factures/dossier/${dossierId}`, { headers }),
        axios.get(`${API_URL}/dossiers-financeurs?action_id=${dossierId}`, { headers }),
      ]);
      setDocuments(docsRes.data);
      setDossiersFinanceurs(dfsRes.data);
      onChanged?.(docsRes.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [dossierId, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  // Facturation a la chaine : ouverture directe du choix des mois (periode pre-cochee) pour un DF donne
  const autoDone = React.useRef(null);
  useEffect(() => {
    if (!autoFacture || loading || autoDone.current === `${autoFacture.dfId}|${autoFacture.periode}`) return;
    const df = dossiersFinanceurs.find(x => x.id === autoFacture.dfId);
    if (!df) return;
    autoDone.current = `${autoFacture.dfId}|${autoFacture.periode}`;
    if (MODALITES_JOUR.includes(df.modalite_facturation) && autoFacture.periode) { setSelectedMonths([autoFacture.periode]); setFactureDialog(df); }
    else generateDocument('facture', df);
  }, [autoFacture, loading, dossiersFinanceurs]); // eslint-disable-line react-hooks/exhaustive-deps

  // BDC pré-cochés : ceux du mois de la facture (+ ceux sans mois) ; tous si facture sans période
  const defaultBdcIdx = (df, periode) => (df.bons_de_commande || [])
    .map((b, bi) => ({ b, bi }))
    .filter(({ b }) => !periode || !b.mois || b.mois === periode)
    .map(({ bi }) => bi);
  const openRevue = (df, typeDoc, items) => setRevue({ df, typeDoc, items: items.map(it => ({ ...it, bdc_idx: defaultBdcIdx(df, it.periode) })) });

  const generateDocument = async (typeDoc, df) => {
    if (!df) return;
    // Facture en modalité "à la journée/heure" -> choix des périodes mensuelles
    if (typeDoc === 'facture' && MODALITES_JOUR.includes(df.modalite_facturation)) {
      setSelectedMonths([]);
      setFactureDialog(df);
      return;
    }
    const nbJours = formData.planning?.length || 0;
    const nbCandidats = (df.candidats || []).length;
    let quantite = 0;
    if (df.modalite_facturation === 'journee') quantite = nbJours;
    else if (df.modalite_facturation === 'heure_formation') quantite = nbJours * 7;
    else if (df.modalite_facturation === 'heure_stagiaire') quantite = nbJours * 7 * nbCandidats;
    else if (df.modalite_facturation === 'forfait') quantite = 1;
    else if (df.modalite_facturation === 'par_candidat') quantite = nbCandidats;
    if (prestation && !quantite) quantite = 1;
    const unite = { journee: 'jour', heure_formation: 'heure', heure_stagiaire: 'heure stagiaire', forfait: 'forfait', par_candidat: 'candidat' }[df.modalite_facturation] || 'unité';
    const description = `${formData.intitule || 'Formation'} — ${df.financeur_nom || 'N/A'}${df.nature_financement ? ` (${df.nature_financement})` : ''}`;
    if (typeDoc === 'facture' || typeDoc === 'avoir') {
      // Revue avant génération : prix et quantité (jours / heures) modifiables
      openRevue(df, typeDoc, [{ periode: null, description, quantite, unite, prix_unitaire_ht: df.tarif_unitaire || 0, periodes: undefined }]);
      return;
    }
    await posterDocuments(df, typeDoc, [{ periode: null, description, quantite, unite, prix_unitaire_ht: df.tarif_unitaire || 0 }]);
  };

  // Envoi des documents (un par item) — commun aux flux simple, mensuel et revue
  const posterDocuments = async (df, typeDoc, items) => {
    setGenerating(df.id);
    const fin = financeurs.find(f => f.id === df.financeur_id);
    const allBdcs = df.bons_de_commande || [];
    let created = 0;
    for (const it of items) {
      const quantite = Number(it.quantite) || 0, pu = Number(it.prix_unitaire_ht) || 0;
      const montantHt = Math.round(quantite * pu * 100) / 100;
      const bdcs = (it.bdc_idx ? allBdcs.filter((_, bi) => it.bdc_idx.includes(bi)) : allBdcs).map(b => ({ numero_bdc: b.numero_bdc || '', commentaire: b.commentaire || '' }));
      try {
        await axios.post(`${API_URL}/factures/generer/${dossierId}`, {
          type_document: typeDoc,
          dossier_financeur_id: df.id,
          destinataire_type: 'financeur',
          destinataire_id: df.financeur_id,
          destinataire_nom: df.financeur_nom || '',
          references_bdc: bdcs.map(b => b.numero_bdc).filter(Boolean),
          bdc_details: bdcs,
          code_action_client: df.code_action_client || formData.code_action_client || null,
          ...(it.periode ? { periode: it.periode } : {}),
          lignes: [{ description: it.description, quantite, unite: it.unite, prix_unitaire_ht: pu, montant_ht: montantHt, bdcs, ...(it.periodes ? { periodes: it.periodes } : {}) }],
          montant_ht: montantHt,
          date_emission: new Date().toISOString().split('T')[0],
          mode_reglement: fin?.mode_reglement || 'virement',
        }, { headers });
        created += 1;
      } catch (err) { toast.error(err.response?.data?.detail || `Erreur de génération${it.moisLabel ? ` (${it.moisLabel})` : ''}`); }
    }
    if (created > 0) toast.success(`${created} ${typeDoc}(s) généré(e)(s) pour ${df.financeur_nom}`);
    setRevue(null); setFactureDialog(null); setGenerating(null); fetchAll();
  };

  const quantiteForDays = (df, nbJours) => {
    if (df.modalite_facturation === 'journee') return nbJours;
    if (df.modalite_facturation === 'heure_formation') return nbJours * 7;
    if (df.modalite_facturation === 'heure_stagiaire') return nbJours * 7 * (df.candidats || []).length;
    return nbJours;
  };

  const generateMonthlyFactures = () => {
    const df = factureDialog;
    if (!df || selectedMonths.length === 0) return;
    const unite = { journee: 'jour', heure_formation: 'heure', heure_stagiaire: 'heure stagiaire' }[df.modalite_facturation] || 'jour';
    const items = [...selectedMonths].sort().map(month => {
      const dates = planningMonths[month] || [];
      const moisLabel = format(parseISO(`${month}-01`), 'MMMM yyyy', { locale: fr });
      return { periode: month, moisLabel, description: `${formData.intitule || 'Formation'} — ${df.financeur_nom || 'N/A'}${df.nature_financement ? ` (${df.nature_financement})` : ''} — Période ${moisLabel}`, quantite: quantiteForDays(df, dates.length), unite, prix_unitaire_ht: df.tarif_unitaire || 0, periodes: buildWeeklyPeriods(dates), nbJours: dates.length };
    });
    openRevue(df, 'facture', items);
  };

  const openManualDialog = (df) => {
    setManualLignes([{ description: prestation ? (formData.intitule || '') : '', quantite: 1, unite: 'forfait', prix_unitaire_ht: df.tarif_unitaire || 0 }]);
    setManualMeta({ periode: '', bdc_idx: defaultBdcIdx(df, null), numero: '', numeroValide: true });
    setManualDialog(df);
  };
  const setManualPeriode = (periode) => setManualMeta(m => ({ ...m, periode, bdc_idx: defaultBdcIdx(manualDialog, periode || null) }));

  const generateManualFacture = async () => {
    const df = manualDialog;
    if (!manualMeta.numeroValide) { toast.error('Ce numéro est déjà utilisé par un autre document'); return; }
    const lignes = manualLignes
      .filter(l => l.description.trim())
      .map(l => ({ ...l, quantite: Number(l.quantite) || 0, prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0, montant_ht: (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0) }));
    if (!df || lignes.length === 0) { toast.info('Ajoutez au moins une ligne avec une désignation'); return; }
    setGenerating(df.id);
    const fin = financeurs.find(f => f.id === df.financeur_id);
    const montantHt = lignes.reduce((s, l) => s + l.montant_ht, 0);
    const bdcs = (df.bons_de_commande || []).filter((_, bi) => manualMeta.bdc_idx.includes(bi)).map(b => ({ numero_bdc: b.numero_bdc || '', commentaire: b.commentaire || '' }));
    const moisLabel = manualMeta.periode ? format(parseISO(`${manualMeta.periode}-01`), 'MMMM yyyy', { locale: fr }) : null;
    try {
      await axios.post(`${API_URL}/factures/generer/${dossierId}`, {
        type_document: 'facture',
        numero: (manualMeta.numero || '').trim() || undefined,
        facture_manuelle: !prestation,
        dossier_financeur_id: df.id,
        destinataire_type: 'financeur',
        destinataire_id: df.financeur_id,
        destinataire_nom: df.financeur_nom || '',
        references_bdc: bdcs.map(b => b.numero_bdc).filter(Boolean),
        bdc_details: bdcs,
        code_action_client: df.code_action_client || formData.code_action_client || null,
        ...(manualMeta.periode ? { periode: manualMeta.periode } : {}),
        lignes: lignes.map((l, i) => ({ ...l, ...(i === 0 ? { bdcs, ...(moisLabel ? { periodes: [`Période ${moisLabel}`] } : {}) } : { bdcs: [] }) })),
        montant_ht: montantHt,
        date_emission: new Date().toISOString().split('T')[0],
        mode_reglement: fin?.mode_reglement || 'virement',
      }, { headers });
      toast.success(`Facture générée pour ${df.financeur_nom}`);
      setManualDialog(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de génération'); }
    finally { setGenerating(null); }
  };

  const updateDocStatut = async (docId, newStatut) => {
    try {
      await axios.patch(`${API_URL}/factures/${docId}/statut`, {
        statut: newStatut,
        date_paiement: newStatut === 'paye' ? new Date().toISOString().split('T')[0] : null
      }, { headers });
      toast.success('Statut mis à jour');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const docStatutColor = (s) => ({
    brouillon: 'bg-slate-100 text-slate-700', emis: 'bg-blue-100 text-blue-700',
    envoye: 'bg-indigo-100 text-indigo-700', signe: 'bg-purple-100 text-purple-700',
    paye: 'bg-green-100 text-green-700', annule: 'bg-red-100 text-red-700',
    avoir_emis: 'bg-orange-100 text-orange-700',
  }[s] || 'bg-slate-100 text-slate-700');

  const typeDocLabel = (t) => ({
    facture: 'Facture', devis: 'Devis', convention: 'Convention', avoir: 'Avoir',
    certificat_realisation: 'Certificat de réalisation', contrat_formation: 'Contrat',
    bon_commande: 'Bon de commande', echeancier: 'Échéancier',
  }[t] || t);

  if (loading) return <Card><CardContent className="py-8 text-center text-slate-400">Chargement...</CardContent></Card>;

  return (
    <Card className="border-0 shadow-sm" data-testid="facturation-tab">
      <CardContent className="p-6 space-y-6">
        <h3 className="text-lg font-semibold text-slate-900">Facturation & Documents</h3>

        {dossiersFinanceurs.length === 0 ? (
          <div className="text-center py-8">
            <CreditCard className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Aucun dossier financeur créé.</p>
            <p className="text-xs text-slate-400">Créez d'abord des dossiers financeurs dans l'onglet "Dossiers financeurs" pour générer des documents.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {dossiersFinanceurs.map(df => {
              const dfDocs = documents.filter(d => d.dossier_financeur_id === df.id);
              const isGenerating = generating === df.id;
              const nbBdc = (df.bons_de_commande || []).length;

              return (
                <div key={df.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`facturation-df-${df.id}`}>
                  {/* DF Header */}
                  <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{df.code}</span>
                      <span className="text-sm font-semibold text-slate-800">{df.financeur_nom || 'N/A'}</span>
                      {df.nature_financement && <span className="text-xs text-indigo-500">{df.nature_financement}</span>}
                      <span className="text-xs text-slate-400">
                        {(df.candidats || []).length} candidat(s) — {df.tarif_unitaire || 0} € / {df.modalite_facturation}
                      </span>
                      {nbBdc > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{nbBdc} BDC</span>}
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" variant="outline" size="sm" onClick={() => generateDocument('devis', df)} disabled={isGenerating} className="text-xs h-7">
                        <FileText className="w-3 h-3 mr-1 text-blue-600" /> Devis
                      </Button>
                      {!prestation && (
                        <Button type="button" variant="outline" size="sm" onClick={() => generateDocument('convention', df)} disabled={isGenerating} className="text-xs h-7">
                          <FileCheck className="w-3 h-3 mr-1 text-purple-600" /> Convention
                        </Button>
                      )}
                      {!prestation && (
                        <Button type="button" variant="outline" size="sm" onClick={() => generateDocument('facture', df)} disabled={isGenerating} className="text-xs h-7" data-testid={`btn-facture-${df.id}`}>
                          <Receipt className="w-3 h-3 mr-1 text-emerald-600" /> Facture
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={() => openManualDialog(df)} disabled={isGenerating} className="text-xs h-7" data-testid={`btn-facture-manuelle-${df.id}`}>
                        {prestation ? <Receipt className="w-3 h-3 mr-1 text-emerald-600" /> : <Plus className="w-3 h-3 mr-1 text-amber-600" />} {prestation ? 'Facture' : 'Facture manuelle'}
                      </Button>
                    </div>
                  </div>
                  {/* Documents for this DF */}
                  {dfDocs.length > 0 && (
                    <div className="divide-y">
                      <div className="grid grid-cols-[70px_130px_1fr_100px_90px_90px_80px_75px] gap-2 bg-white border-t px-4 py-1.5 text-[10px] font-medium text-slate-400 uppercase">
                        <span>Type</span><span>Numéro</span><span>Destinataire</span><span>Réf. BDC</span><span>HT</span><span>Statut</span><span>Date</span><span>Actions</span>
                      </div>
                      {dfDocs.map(doc => (
                        <div key={doc.id} className="grid grid-cols-[70px_130px_1fr_100px_90px_90px_80px_75px] gap-2 items-center px-4 py-2 hover:bg-slate-50" data-testid={`document-row-${doc.id}`}>
                          <span className="text-[11px] font-medium text-slate-700">{typeDocLabel(doc.type_document)}</span>
                          <span className="text-xs font-mono text-indigo-600 flex items-center gap-1 min-w-0"><span className="truncate">{doc.numero}</span><button type="button" onClick={() => openPreview(doc)} className="text-slate-400 hover:text-indigo-600 shrink-0" title="Visualiser la facture" data-testid={`doc-preview-${doc.id}`}><Eye className="w-3.5 h-3.5" /></button></span>
                          <div className="text-xs text-slate-600 truncate">
                            {doc.destinataire_nom}
                            {doc.code_action_client && <span className="ml-1 text-[10px] text-purple-600 font-mono">[{doc.code_action_client}]</span>}
                            {doc.periode && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">{format(parseISO(`${doc.periode}-01`), 'MMM yyyy', { locale: fr })}</span>}
                            {doc.facture_manuelle && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">manuelle</span>}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(doc.references_bdc || []).map((ref) => (
                              <span key={ref} className="text-[10px] font-mono bg-orange-100 text-orange-700 px-1 py-0.5 rounded">{ref}</span>
                            ))}
                            {(!doc.references_bdc || doc.references_bdc.length === 0) && <span className="text-[10px] text-slate-400">—</span>}
                          </div>
                          <span className="text-xs font-medium text-slate-900">{doc.montant_ht?.toFixed(2)} €</span>
                          <Select value={doc.statut} onValueChange={(v) => updateDocStatut(doc.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] border-0 bg-transparent px-0">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${docStatutColor(doc.statut)}`}>{doc.statut}</span>
                            </SelectTrigger>
                            <SelectContent>
                              {['brouillon', 'emis', 'envoye', 'signe', 'paye', 'annule', 'avoir_emis'].map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-[11px] text-slate-400">{doc.date_emission || '-'}</span>
                          <div className="flex items-center gap-0.5">
                            {['facture', 'avoir'].includes(doc.type_document) && <FactureVerrouActions doc={doc} onEdit={setEditingFacture} onChanged={fetchAll} />}
                            <button
                              type="button"
                              onClick={() => downloadPdf(doc.id, doc.numero, nomFichier(doc))}
                              className="flex items-center justify-center w-7 h-7 rounded hover:bg-indigo-50 text-indigo-600"
                              title="Télécharger PDF"
                              data-testid={`pdf-download-${doc.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {dfDocs.length === 0 && (
                    <div className="px-4 py-3 text-xs text-slate-400 text-center border-t">Aucun document généré</div>
                  )}
                </div>
              );
            })}

            {/* Documents sans dossier financeur (legacy) */}
            {documents.filter(d => !d.dossier_financeur_id).length > 0 && (
              <div className="border border-amber-200 rounded-lg overflow-hidden">
                <div className="bg-amber-50 px-4 py-3">
                  <span className="text-sm font-medium text-amber-800">Documents historiques (sans dossier financeur)</span>
                </div>
                <div className="divide-y">
                  {documents.filter(d => !d.dossier_financeur_id).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-medium text-slate-700">{typeDocLabel(doc.type_document)}</span>
                        <span className="text-xs font-mono text-indigo-600 flex items-center gap-1">{doc.numero}<button type="button" onClick={() => openPreview(doc)} className="text-slate-400 hover:text-indigo-600" title="Visualiser" data-testid={`doc-preview-${doc.id}`}><Eye className="w-3.5 h-3.5" /></button></span>
                        <span className="text-xs text-slate-500">{doc.destinataire_nom}</span>
                        <span className="text-xs font-medium">{doc.montant_ht?.toFixed(2)} €</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${docStatutColor(doc.statut)}`}>{doc.statut}</span>
                        <button type="button" onClick={() => downloadPdf(doc.id, doc.numero, nomFichier(doc))} className="text-indigo-600">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <FactureEditDialog doc={editingFacture} open={!!editingFacture} onClose={() => setEditingFacture(null)} onSaved={fetchAll} />

        {/* Dialog: sélection des périodes mensuelles à facturer */}
        <Dialog open={!!revue} onOpenChange={(o) => { if (!o) setRevue(null); }}>
        <DialogContent className="max-w-3xl" data-testid="revue-facture-dialog">
          <DialogHeader>
            <DialogTitle>Vérifier avant de générer {revue?.typeDoc === 'avoir' ? "l'avoir" : 'la facture'}</DialogTitle>
            <DialogDescription>{revue?.df?.financeur_nom} — modifiez si besoin le prix unitaire et le nombre de {revue?.items?.[0]?.unite === 'jour' ? 'jours' : revue?.items?.[0]?.unite?.startsWith('heure') ? 'heures' : 'unités'} avant la génération.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_110px_90px_120px_110px] gap-2 text-[11px] font-semibold text-slate-500 px-1"><span>Désignation</span><span>Quantité</span><span>Unité</span><span>Prix unitaire HT</span><span className="text-right">Montant HT</span></div>
            {(revue?.items || []).map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_90px_120px_110px] gap-2 items-center rounded-lg border p-2 bg-slate-50/50" data-testid={`revue-ligne-${i}`}>
                <div className="min-w-0">
                  {it.moisLabel && <p className="text-xs font-semibold text-slate-800 capitalize">{it.moisLabel}{it.nbJours != null ? <span className="text-slate-400 font-normal"> · {it.nbJours} jour(s) planifié(s)</span> : null}</p>}
                  <Input value={it.description} onChange={(e) => setRevueItem(i, { description: e.target.value })} className="h-8 text-xs mt-0.5" data-testid={`revue-description-${i}`} />
                  {(revue?.df?.bons_de_commande || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1" data-testid={`revue-bdcs-${i}`}>
                      <span className="text-[10px] uppercase text-slate-400 self-center">BDC sur cette facture :</span>
                      {(revue.df.bons_de_commande || []).map((b, bi) => {
                        const sel = (it.bdc_idx || []).includes(bi);
                        return (
                          <label key={bi} className={`flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 cursor-pointer border ${sel ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-white border-slate-200 text-slate-400'}`} title={b.commentaire || ''}>
                            <Checkbox checked={sel} onCheckedChange={(v) => setRevueItem(i, { bdc_idx: v ? [...(it.bdc_idx || []), bi] : (it.bdc_idx || []).filter(x => x !== bi) })} className="h-3 w-3" data-testid={`revue-bdc-${i}-${bi}`} />
                            <span className="font-mono">{b.numero_bdc || `BDC ${bi + 1}`}</span>
                            {b.mois && <span className="text-[10px] bg-white/70 border border-current/20 rounded px-1 capitalize">{format(parseISO(`${b.mois}-01`), 'MMM yyyy', { locale: fr })}</span>}
                            {b.commentaire && <span className="truncate max-w-[160px] italic">— {b.commentaire}</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Input type="number" step="0.5" min="0" value={it.quantite} onChange={(e) => setRevueItem(i, { quantite: e.target.value })} className="h-8 text-xs" data-testid={`revue-quantite-${i}`} />
                <span className="text-xs text-slate-600 truncate">{it.unite}</span>
                <Input type="number" step="0.01" min="0" value={it.prix_unitaire_ht} onChange={(e) => setRevueItem(i, { prix_unitaire_ht: e.target.value })} className="h-8 text-xs" data-testid={`revue-prix-${i}`} />
                <span className="text-sm font-semibold text-right" data-testid={`revue-montant-${i}`}>{((Number(it.quantite) || 0) * (Number(it.prix_unitaire_ht) || 0)).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <span className="text-xs text-slate-500">{(revue?.items || []).length} document(s) à générer</span>
            <span className="text-right"><span className="block text-base font-bold text-slate-900" data-testid="revue-total">Total HT : {revueTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span><span className="block text-[11px] text-slate-500" data-testid="revue-tva">TVA {tauxTva} % : {(revueTotal * tauxTva / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} · TTC : {(revueTotal * (1 + tauxTva / 100)).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span></span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRevue(null)}>Annuler</Button>
            <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!generating || (revue?.items || []).some(it => !(Number(it.quantite) > 0))} onClick={() => posterDocuments(revue.df, revue.typeDoc, revue.items)} data-testid="revue-generer-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Receipt className="w-4 h-4 mr-1" /> Générer {revue?.items?.length > 1 ? `${revue.items.length} factures` : revue?.typeDoc === 'avoir' ? "l'avoir" : 'la facture'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col" data-testid="doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4 text-indigo-600" /> {preview?.numero}{preview?.nom ? ` — ${preview.nom}` : ''}</DialogTitle>
            <DialogDescription>Aperçu du document avant édition.</DialogDescription>
          </DialogHeader>
          {preview && <iframe title="Aperçu" src={preview.url} className="flex-1 w-full rounded border" data-testid="doc-preview-frame" />}
          <DialogFooter>
            {preview && <Button type="button" variant="ghost" onClick={() => window.open(preview.url, '_blank')} className="mr-auto" data-testid="doc-preview-newtab">Ouvrir dans un nouvel onglet</Button>}
            <Button type="button" variant="outline" onClick={closePreview}>Fermer</Button>
            {preview && <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => downloadPdf(preview.doc.id, preview.numero, nomFichier(preview.doc))} data-testid="doc-preview-download"><Download className="w-4 h-4 mr-1" /> Télécharger</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!factureDialog} onOpenChange={(o) => !o && setFactureDialog(null)}>
          <DialogContent className="max-w-md" data-testid="facture-periodes-dialog">
            <DialogHeader>
              <DialogTitle>Facturer — {factureDialog?.financeur_nom}</DialogTitle>
              <DialogDescription className="text-xs">Sélectionnez le ou les mois à facturer. Une facture est générée par mois, détaillant les périodes hebdomadaires. Un mois déjà facturé ne peut plus être édité automatiquement.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {factureDialog && Object.keys(planningMonths).sort().map(month => {
                const dates = planningMonths[month];
                const already = invoicedPeriods(factureDialog.id).has(month);
                const checked = selectedMonths.includes(month);
                const q = quantiteForDays(factureDialog, dates.length);
                const montant = q * (factureDialog.tarif_unitaire || 0);
                return (
                  <label key={month} className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${already ? 'opacity-50 bg-slate-50' : 'cursor-pointer hover:bg-slate-50'}`} data-testid={`periode-mois-${month}`}>
                    <Checkbox
                      checked={checked}
                      disabled={already}
                      onCheckedChange={(v) => setSelectedMonths(prev => v ? [...prev, month] : prev.filter(m => m !== month))}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800 capitalize">{format(parseISO(`${month}-01`), 'MMMM yyyy', { locale: fr })}</p>
                      <p className="text-[11px] text-slate-400">{dates.length} jour(s) — {montant.toFixed(2)} € HT</p>
                    </div>
                    {already && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Déjà facturé</span>}
                  </label>
                );
              })}
              {factureDialog && Object.keys(planningMonths).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Aucune date au planning</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFactureDialog(null)}>Annuler</Button>
              <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" disabled={selectedMonths.length === 0 || !!generating} onClick={generateMonthlyFactures} data-testid="confirm-facture-periodes-btn">
                Générer {selectedMonths.length || ''} facture(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: facture manuelle (actions extras) */}
        <Dialog open={!!manualDialog} onOpenChange={(o) => !o && setManualDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="facture-manuelle-dialog">
            <DialogHeader>
              <DialogTitle>{prestation ? 'Facture' : 'Facture manuelle'} — {manualDialog?.financeur_nom}</DialogTitle>
              <DialogDescription className="text-xs">{prestation ? 'Facture libre : saisissez les lignes, la période facturée et les bons de commande à référencer.' : 'Pour facturer des actions extras (hors facturation automatique par période).'}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-[180px_1fr] gap-3 items-start bg-slate-50 rounded-lg p-3" data-testid="manual-meta">
              <div className="space-y-1">
                <span className="text-[10px] uppercase text-slate-400">Période facturée (mois)</span>
                <Input type="month" value={manualMeta.periode} onChange={(e) => setManualPeriode(e.target.value)} className="h-8 text-xs bg-white" data-testid="manual-periode" />
              </div>
              {manualDialog && <NumeroDocumentField compact value={manualMeta.numero} onChange={(v) => setManualMeta(m => ({ ...m, numero: v }))} typeDocument="facture" periode={manualMeta.periode || undefined} onValidite={(ok) => setManualMeta(m => ({ ...m, numeroValide: ok }))} />}
              <div className="space-y-1 col-span-2">
                <span className="text-[10px] uppercase text-slate-400">Bons de commande référencés sur la facture</span>
                {(manualDialog?.bons_de_commande || []).length === 0 ? <p className="text-xs text-slate-400 italic">Aucun BDC sur ce dossier financeur — la facture sera émise sans référence BDC.</p> : (
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {(manualDialog?.bons_de_commande || []).map((b, bi) => {
                      const sel = manualMeta.bdc_idx.includes(bi);
                      return (
                        <label key={bi} className={`flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 cursor-pointer border ${sel ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-white border-slate-200 text-slate-400'}`} title={b.commentaire || ''}>
                          <Checkbox checked={sel} onCheckedChange={(v) => setManualMeta(m => ({ ...m, bdc_idx: v ? [...m.bdc_idx, bi] : m.bdc_idx.filter(x => x !== bi) }))} className="h-3 w-3" data-testid={`manual-bdc-${bi}`} />
                          <span className="font-mono">{b.numero_bdc || `BDC ${bi + 1}`}</span>
                          {b.mois && <span className="text-[10px] bg-white/70 border border-current/20 rounded px-1 capitalize">{format(parseISO(`${b.mois}-01`), 'MMM yyyy', { locale: fr })}</span>}
                          {b.commentaire && <span className="truncate max-w-[160px] italic">— {b.commentaire}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 text-[10px] font-medium text-slate-400 uppercase px-1">
                <span>Désignation</span><span>Qté</span><span>Unité</span><span>PU HT (€)</span><span></span>
              </div>
              {manualLignes.map((l, i) => (
                <div key={`ml-${i}`} className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 items-center">
                  <Input value={l.description} onChange={(e) => setManualLignes(prev => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Désignation" className="h-8 text-xs" data-testid={`manual-ligne-desc-${i}`} />
                  <Input type="number" value={l.quantite} onChange={(e) => setManualLignes(prev => prev.map((x, j) => j === i ? { ...x, quantite: e.target.value } : x))} className="h-8 text-xs" />
                  <Input value={l.unite} onChange={(e) => setManualLignes(prev => prev.map((x, j) => j === i ? { ...x, unite: e.target.value } : x))} className="h-8 text-xs" />
                  <Input type="number" step="0.01" value={l.prix_unitaire_ht} onChange={(e) => setManualLignes(prev => prev.map((x, j) => j === i ? { ...x, prix_unitaire_ht: e.target.value } : x))} className="h-8 text-xs" />
                  <button type="button" onClick={() => setManualLignes(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => setManualLignes(prev => [...prev, { description: '', quantite: 1, unite: 'forfait', prix_unitaire_ht: 0 }])}>
                <Plus className="w-3 h-3 mr-1" /> Ajouter une ligne
              </Button>
              <div className="text-right text-sm font-semibold text-slate-800">
                Total HT : {manualLignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0), 0).toFixed(2)} € <span className="font-normal text-slate-500 text-xs">(+ TVA {tauxTva} % = {(manualLignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0), 0) * (1 + tauxTva / 100)).toFixed(2)} € TTC)</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setManualDialog(null)}>Annuler</Button>
              <Button type="button" className={prestation ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'} disabled={generating} onClick={generateManualFacture} data-testid="confirm-facture-manuelle-btn">
                {prestation ? 'Générer la facture' : 'Générer la facture manuelle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export { FacturationTab };
